// Implements: F-UIX-004
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { THEME_STORAGE_KEY } from "./storageKey";

/**
 * Three states, because two cannot express "follow the system", which is the
 * default F-UIX-004 asks for. `system` removes the attribute and lets
 * `prefers-color-scheme` decide; `light` and `dark` write `data-theme` on the
 * root element, which is what `themes.css` overrides on.
 */
export type Theme = "system" | "light" | "dark";

/** What the system resolves to once the choice is applied. */
export type ResolvedTheme = "light" | "dark";

const DARK_QUERY = "(prefers-color-scheme: dark)";

export interface ThemeContextValue {
  readonly theme: Theme;
  readonly resolvedTheme: ResolvedTheme;
  readonly setTheme: (next: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function isTheme(value: string | null): value is Theme {
  return value === "system" || value === "light" || value === "dark";
}

/**
 * The remembered choice, or `system`.
 *
 * Guarded (checklist 15): where site data is blocked the storage call itself throws
 * `SecurityError`, and this is the state initialiser of the outermost provider, with no
 * boundary above it, so an unguarded read is a blank document on every screen.
 */
function readPersistedTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

/** Remember the choice where storage allows it; a refusal only loses it for the next visit. */
function persistTheme(next: Theme): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch {
    // Storage denied: the theme still applies to this page.
  }
}

export interface ThemeProviderProps {
  readonly children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps): ReactNode {
  const [theme, setPersistedTheme] = useState<Theme>(readPersistedTheme);
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(
    () => window.matchMedia(DARK_QUERY).matches,
  );

  useEffect(() => {
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent): void => {
      setSystemPrefersDark(event.matches);
    };
    query.addEventListener("change", onChange);
    return () => {
      query.removeEventListener("change", onChange);
    };
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "system") {
      root.removeAttribute("data-theme");
    } else {
      root.dataset.theme = theme;
    }
  }, [theme]);

  const setTheme = useCallback((next: Theme): void => {
    persistTheme(next);
    setPersistedTheme(next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      resolvedTheme: theme === "system" ? (systemPrefersDark ? "dark" : "light") : theme,
      setTheme,
    }),
    [theme, systemPrefersDark, setTheme],
  );

  return <ThemeContext value={value}>{children}</ThemeContext>;
}

/** The theme choice and the setter, from the nearest provider. */
export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (value === null) {
    throw new Error("useTheme must be called inside a ThemeProvider");
  }
  return value;
}
